import DurationFacet, { RangeEntry } from './DurationFacetPresentation';
import * as helper from '../../../utils/misc';
import { FacetLogic, RenderFacetProps } from './Types';
import { Field } from '../../../types/Field';
import { DurationFacetValue, FacetValue, FacetValueCount, FacetValues, ReplayAPI_DurationFacetValue } from '../../../types/Facet';

export const DURATION_MINIMUM_VALUE = 0;
export const DURATION_MAXIMUM_VALUE = 24 * 60 * 60;

const durationRangeDefinitions = [
    { displayName: "Short", lowerValue: DURATION_MINIMUM_VALUE, upperValue: 5 * 60},
    { displayName: "Standard", lowerValue: 5 * 60, upperValue: 60 * 60},
    { displayName: "Long", lowerValue: 60 * 60, upperValue: DURATION_MAXIMUM_VALUE},
]

function getLowerValue(facetSelections: FacetValues<DurationFacetValue>) {
    if (!facetSelections || facetSelections.facetValues === undefined || facetSelections.facetValues.length === 0) {
        return DURATION_MINIMUM_VALUE;
    }

    if (facetSelections.facetValues.length > 1) 
    {
        throw new Error(`Duration facet selection should only contain one value, but found ${facetSelections.facetValues.length} values.`);
    }

    return facetSelections.facetValues[0].lowerValue;
}

function getUpperValue(facetSelection: FacetValues<DurationFacetValue>) {
    if (!facetSelection || facetSelection.facetValues === undefined || facetSelection.facetValues.length === 0) {
        return DURATION_MAXIMUM_VALUE;
    }

    if (facetSelection.facetValues.length > 1) 
    {
        throw new Error(`Duration facet selection should only contain one value, but found ${facetSelection.facetValues.length} values.`);
    }

    return facetSelection.facetValues[0].upperValue;
}

function toRangeEntries(lowerValue: any, upperValue: any, facetValues: FacetValueCount<DurationFacetValue>[]) : RangeEntry[] {

    return facetValues.map((facetValue: FacetValueCount<DurationFacetValue>) => {
        let rangeDef = durationRangeDefinitions[facetValue.facetValue.periodIndex];
        let checked = false;
        if (lowerValue === rangeDef.lowerValue && upperValue === rangeDef.upperValue){
            checked = true;
        }         

        return {            
            lowerValue: facetValue.facetValue.lowerValue,
            upperValue: facetValue.facetValue.upperValue,
            count: facetValue.count,
            checked: checked,
            displayName: rangeDef.displayName,
        };
    });
}

function createFilter(facetSelection: FacetValues<DurationFacetValue>, field: Field) {
    const lowerValue = facetSelection.facetValues[0].lowerValue.toString();
    const upperValue = facetSelection.facetValues[0].upperValue.toString();

    if (helper.isEmpty(lowerValue) || helper.isEmpty(upperValue)) {
        return "";
    }
    
    const fromFilter = field.systemSettings.filterClause.replace('{{VALUE}}', lowerValue).replace('{{OPERATOR}}', 'gt')
    const toFilter = field.systemSettings.filterClause.replace('{{VALUE}}', upperValue).replace('{{OPERATOR}}', 'lt')

    return `(${fromFilter} and ${toFilter})`;
}

function createRequest(field: Field) {
    let values = "";

    for (let i = 0; i < durationRangeDefinitions.length; i++) {
        if (values !== "") {
            values = values + "|";
        }
        values = values + durationRangeDefinitions[i].upperValue;
    }

    if (values.length > 0) {
        return field.name + ",values:" + values
    }

    return field.name;
}

function extractFacetValues(facetName: string, facetValuesFromSearch: ReplayAPI_DurationFacetValue[]) : FacetValueCount<FacetValue>[] {
    
    let rangeValues: FacetValueCount<DurationFacetValue>[] = [];
    // Skip the last facet result (more than 24h is not presented)
    for (let i = 0, facetIndex = 0; i < facetValuesFromSearch.length - 1; i++, facetIndex++) {
        rangeValues.push({
            facetValue: new DurationFacetValue(
                facetName,
                facetIndex,
                durationRangeDefinitions[facetIndex].lowerValue,
                durationRangeDefinitions[facetIndex].upperValue
            ),
            count: facetValuesFromSearch[i].count,
        })
    }
    
    return rangeValues;
}

function renderFacet (props: RenderFacetProps) {
    let lowerValue = getLowerValue(props.facetSelection as FacetValues<DurationFacetValue>);
    let upperValue = getUpperValue(props.facetSelection as FacetValues<DurationFacetValue>);

    let rangeEntries = toRangeEntries(lowerValue, upperValue, props.facetValues as FacetValueCount<DurationFacetValue>[]);

    return <DurationFacet 
        key={props.field.name} 
        name={props.field.name} 
        displayName={props.field.userSettings.displayName} 
        rangeData={rangeEntries} 
        lowerValue={lowerValue}
        upperValue={upperValue}
        minValue={DURATION_MINIMUM_VALUE} 
        maxValue={DURATION_MAXIMUM_VALUE} 
        onChangeFacet={props.onChangeFacetSelection}/>
}

// The facetValue contains an object with the new lower and upper value. 
// The returned selection of this facet type is not based upon the previous selections, 
// the received value is simply returned.
function createUpdatedSelection(facetValue: DurationFacetValue, prevFacetSelection: FacetValues<DurationFacetValue>): FacetValues<DurationFacetValue> {
    return {
        facetName: prevFacetSelection.facetName,
        facetValues: [facetValue],
    };
}

export const durationFacetLogic: FacetLogic = {
    dataType: 'Edm.Duration',
    createFilter: createFilter,
    createRequest: createRequest,
    extractFacetValues: extractFacetValues,
    renderFacet: renderFacet,
    createUpdatedSelection: createUpdatedSelection,
}